import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  createDowntimeReason,
  deleteDowntimeReason,
  listDowntimeReasons,
  updateDowntimeReason,
} from "../api/downtimeReasons";
import { EmptyState, PageHeader, StatusBadge } from "../components/ui";

const EMPTY_FORM = { code: "", name: "", status: "active" };

export default function DowntimeReasons() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  async function load() {
    try {
      setLoadError("");
      setItems(await listDowntimeReasons(true));
    } catch (err) {
      setLoadError(err?.response?.data?.error?.message || "Duruş sebepleri yüklenemedi.");
      setItems([]);
    }
  }

  useEffect(() => { load(); }, []);

  function openEditor(item = null) {
    setSelected(item);
    setForm(item ? { code: item.code || "", name: item.name || "", status: item.status || "active" } : EMPTY_FORM);
    setError("");
    setOpen(true);
  }

  async function save() {
    if (!form.code.trim() || !form.name.trim()) {
      setError("Duruş kodu ve sebebi zorunludur.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (selected?.id) await updateDowntimeReason(selected.id, form);
      else await createDowntimeReason(form);
      setOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Duruş sebebi kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(item) {
    if (!window.confirm(`"${item.name}" pasifleştirilsin mi?`)) return;
    try {
      await deleteDowntimeReason(item.id);
      await load();
    } catch (err) {
      window.alert(err?.response?.data?.error?.message || "Duruş sebebi pasifleştirilemedi.");
    }
  }

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Duruşlar"
        subtitle="Makine bazlı OEE için duruş sebeplerini yönetin."
        actions={<Button variant="contained" onClick={() => openEditor()}>+ Yeni Duruş</Button>}
      />
      {loadError ? <Alert severity="error">{loadError}</Alert> : null}
      {!items.length && !loadError ? (
        <EmptyState description="Henüz duruş sebebi tanımı yok." actionLabel="+ Yeni Duruş" onAction={() => openEditor()} />
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kod</TableCell>
                <TableCell>Duruş sebebi</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell><Typography fontWeight={800}>{item.code}</Typography></TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell><StatusBadge value={item.status} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openEditor(item)}>Düzenle</Button>
                      {item.status === "active" ? (
                        <Button size="small" color="error" variant="outlined" onClick={() => deactivate(item)}>Pasifleştir</Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={open} onClose={saving ? undefined : () => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selected ? "Duruş Sebebini Düzenle" : "Yeni Duruş Sebebi"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Duruş kodu"
              value={form.code}
              onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
              required
              disabled={saving}
            />
            <TextField
              label="Duruş sebebi"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              disabled={saving}
            />
            <Stack direction="row" alignItems="center">
              <Switch
                checked={form.status === "active"}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.checked ? "active" : "inactive" }))}
                disabled={saving}
              />
              <Typography>Aktif</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit" disabled={saving}>İptal</Button>
          <Button onClick={save} variant="contained" disabled={saving}>{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
